import { Lead } from './matchDevice';
import { ReportColor } from './transmissionReport';

export interface VendorDataOverrides {
  'device.batteryVoltage'?: string | null;
  'device.deviceImplantDate'?: string | null;
  'device.batteryStatus'?: string | null;
  'device.batteryMeasurementTime'?: string | null;
  'device.batteryRemainingLongevity'?: string | null;
  'device.batteryPercentRemaining'?: string | null;
  'device.capacitorChargeTime'?: string | null;
  'device.vendorId'?: number | null;
  egmImage?: string | null;
  egmImageStatus?: string | null;

  'deviceMeasurement.afBurden'?: string | null;
  'deviceMeasurement.apAPacePercent'?: string | null;
  'deviceMeasurement.rvpRvPacePercent'?: string | null;
  'deviceMeasurement.bvpPercent'?: string | null;
  'deviceMeasurement.lvpPercent'?: string | null;
  'deviceMeasurement.hvImpedance'?: string | null;

  'deviceMeasurement.symptomCounters'?: string | null;
  'deviceMeasurement.symptomTime'?: string | null;
  'deviceMeasurement.tachyCounters'?: string | null;
  'deviceMeasurement.tachyTime'?: string | null;
  'deviceMeasurement.pauseCounters'?: string | null;
  'deviceMeasurement.pauseTime'?: string | null;
  'deviceMeasurement.bradyCounters'?: string | null;
  'deviceMeasurement.bradyTime'?: string | null;
  'deviceMeasurement.atCounters'?: string | null;
  'deviceMeasurement.atTime'?: string | null;
  'deviceMeasurement.afCounters'?: string | null;
  'deviceMeasurement.afTime'?: string | null;
  'deviceMeasurement.atafCounters'?: string | null;
  'deviceMeasurement.atafTime'?: string | null;

  deviceLeadId?: number | null;
  'deviceLeads.pulsewidth'?: string | null;
  'deviceLeads.sensing'?: string | null;
  'deviceLeads.impedance'?: string | null;
  'deviceLeads.threshold'?: string | null;
  'deviceLeads.leadModelNumber'?: string | null;
  'deviceLeads.vendorId'?: number | null;
  'vendor.vendorName'?: string | null;

  'deviceCounters.symptomCounters'?: string | null;
  'deviceCounters.symptomTable'?: string | null;
  'deviceCounters.tachyCounters'?: string | null;
  'deviceCounters.tachyTime'?: string | null;
  'deviceCounters.pauseCounters'?: string | null;
  'deviceCounters.pauseTime'?: string | null;
  'deviceCounters.bradyCounters'?: string | null;
  'deviceCounters.bradyTime'?: string | null;
  'deviceCounters.ATCounters'?: string | null;
  'deviceCounters.ATTime'?: string | null;
  'deviceCounters.AFCounters'?: string | null;
  'deviceCounters.AFTime'?: string | null;
  'deviceCounters.TimeInATAFCounters'?: string | null;
  'deviceCounters.TimeInATAFTime'?: string | null;
}

export interface UpdateDeviceLeadDto {
  deviceLeadId: number;
  pulsewidth: string | null;
  sensing: string | null;
  impedance: string | null;
  threshold: string | null;
  leadModelNumber: string | null;
  vendorId: number | null;
  leadImplantDate: string | null;
  leadSerialNumber: string | null;
  svcImpedance?: string | null;
}

export interface MultipleVendorDataOverrides extends VendorDataOverrides {
  deviceLeads?: Partial<UpdateDeviceLeadDto>[];
  reportsToExclude?: string[];
}

export interface UpdateMultipleOverridesResponse {
  overrides: Record<string, any>;
}

export interface UpdateMultipleOverridesRequest {
  transmissionReportId: number;
  overrides: MultipleVendorDataOverrides;
}

export interface VtTransmission {
  transmissionId: number;
  transmissionDate: string;
  transmissionFileLocation: string | null;
  vendorId: string | null;
  transmissionFileName: string | null;
  deviceExternalId: string | null;
  transmissionCategory: number | null;
  transmissionStatus: number | null;
  patientId: string | null;
  deviceId: number | null;
  accountId: string | null;
  rhythmPatientId: string | null;
  createdAt: string | null;
  recordState: number | null;
  updatedAt: string | null;
  transmissionAt: string | null;
  autoTriagedColor: ReportColor | null;
  triagedAt: string | null;
  protocolExplanation: string | null;
  autoTriageEvaluationId: string | null;
  autoTriageOutcome: string | null;
  autoTriagedAt: string | null;
}

export interface VtDevice {
  deviceId: number;
  deviceType: string | null;
  deviceSerialNumber: string | null;
  deviceModelNumber: string | null;
  deviceExternalId: string | null;
  vendorId: string | null;
  deviceImplantDate: string | null;
  batteryMeasurementTime: string | null;
  batteryStatus: string | null;
  batteryVoltage: string | null;
  batteryRemainingLongevity: string | null;
  batteryPercentRemaining: string | null;
  pacingMode: string | null;
  vendorPacingMode: string | null;
  lowerRate: string | null;
  maxTrackRate: string | null;
  maxSensorRate: string | null;
  sensedAvDelay: string | null;
  pacedAvDelay: string | null;
  capacitorChargeTime: string | null;
  createdAt: string | null;
  recordState: number | null;
  updatedAt: string | null;
  deviceTelemetry?: string | null;
  mriCompatibility?: string | null;
}

export interface VtMatchDevice {
  modelName: string | null;
  modelNumber: string | null;
  vendor: string | null;
  serialNumber: string | null;
  type: string | null;
  implantDate: string | null;
  id: string;
  status: string | number;
  deviceTelemetry?: string | null;
  isMriCompatibility?: boolean | null;
  isEriTracked: boolean;
  leads?: Lead[] | null;
}

export interface VtDeviceMeasurement {
  id: number;
  deviceExternalId: string | null;
  vendorId: string | null;
  heartRateMonitoringStartInterval: string | null;
  heartRateMonitoringEndInterval: string | null;
  atrialRates: number | null;
  ventricularRates: number | null;
  pacingPercentageStartDate: string | null;
  pacingPercentageEndDate: string | null;
  apAPacePercent: string | null;
  rvpRvPacePercent: string | null;
  apVp: string | null;
  asVp: string | null;
  apVs: string | null;
  asVs: string | null;
  atMonitoringIntervalStartDate: string | null;
  atMonitoringIntervalEndDate: string | null;
  afBurden: string | null;
  amsMsPercent: string | null;
  amsMsPercentPerDay: string | null;
  amsMsCount: number | null;
  amsMsCountPerDay: string | null;
  lvpPercent: string | null;
  bvpPercent: string | null;
  recentShockEpisodes: number | null;
  lifetimeTotalShocks: number | null;
  recentAbortedShocks: number | null;
  lifetimeAbortedShocks: number | null;
  recentAtpEpisodes: number | null;
  lifetimeTotalAtp: number | null;
  lifetimeTherapyTotalStartDate: string | null;
  lifetimeTherapyTotalEndDate: string | null;
  recentTherapyStartDate: string | null;
  recentTherapyEndDate: string | null;
  hvImpedanceMsmtStdt: string | null;
  hvImpedanceMsmtEndt: string | null;
  hvImpedance: string | null;
  transmission: VtTransmission | null;
  createdAt: string | null;
  recordState: number | null;
  updatedAt: string | null;
}

export interface VtDeviceLead {
  leadId: number;
  deviceExternalId: string | null;
  vendorId: string | null;
  vendor?: VtVendor;
  leadLocation: string | null;
  leadModelNumber: string | null;
  leadSerialNumber: string | null;
  leadImplantDate: string | null;
  sensing: string | null;
  threshold: string | null;
  pulsewidth: string | null;
  impedance: string | null;
  svcImpedance?: string | null;
  pacingOutput: string | null;
  pacingPulsewidth: string | null;
  transmission: VtTransmission | null;
  createdAt: string | null;
  recordState: number | null;
  updatedAt: string | null;
  id: string | null;
}

export interface VtVendor {
  vendorId: string;
  vendorName: string | null;
  vendorDescription: string | null;
  vendorCode: string | null;
  createdBy: string | null;
  createdOn: string | null;
  updatedBy: string | null;
  updatedOn: string | null;
}

export enum DeviceLeadLocation {
  RA = 'RA',
  RV = 'RV',
  LV = 'LV'
}

export interface VtLeadVendor {
  /**
   *
   * @type {string}
   * @memberof VtLeadVendor
   */
  leadVendorId: string;
  /**
   *
   * @type {string}
   * @memberof VtLeadVendor
   */
  leadVendorName: string | null;
  /**
   *
   * @type {string}
   * @memberof VtLeadVendor
   */
  leadVendorDescription: string | null;
  /**
   *
   * @type {string}
   * @memberof VtLeadVendor
   */
  leadVendorCode: string | null;
  /**
   *
   * @type {string}
   * @memberof VtLeadVendor
   */
  createdAt: string | null;
  /**
   *
   * @type {string}
   * @memberof VtLeadVendor
   */
  updatedAt: string | null;
}

export interface VendorReportImageResponse {
  name: string;
  key: string;
  lastModified: string;
  bucket: string;
  prefix: string;
  url: string;
  baseEgmUrl: string;
}

export interface ListObjectResponse {
  name: string;
  bucket: string;
  prefix: string | undefined;
  key: string | undefined;
  lastModified: string | undefined;
  eTag: string | undefined;
  size: number | undefined;
  storageClass: string | undefined;
  vendorPDFBaseUrl?: string;
  pdfUrl?: string;
}

export interface VendorReportsResponse {
  vendorTransmissionReports: ListObjectResponse[];
}
